import React, { useState } from 'react';
import {
  CheckCircle2, Clock, XCircle, Package, Truck, Archive, Loader2,
  Edit2, Check, X, ChevronDown, ChevronUp, FileText, Download,
  RotateCcw, StickyNote, MapPin, AlertTriangle, RefreshCw
} from 'lucide-react';
import { base44 } from '@/api/base44Client';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import OrderInternalNotes from './OrderInternalNotes';
import OrderShipmentPanel from './OrderShipmentPanel';
import RoyalMailDispatchPanel from './RoyalMailDispatchPanel';
import OrderRefundPanel from './OrderRefundPanel';
import OrderDownloadsPanel from './OrderDownloadsPanel';
import OrderAuditLogPanel from './OrderAuditLogPanel';

export const STATUS_CONFIG = {
  pending:             { label: 'PENDING',             icon: Clock,         color: 'text-yellow-400', bg: 'bg-yellow-400/10' },
  paid:                { label: 'PAID',                icon: CheckCircle2,  color: 'text-green-400',  bg: 'bg-green-400/10' },
  failed:              { label: 'FAILED',              icon: XCircle,       color: 'text-red-400',    bg: 'bg-red-400/10' },
  in_production:       { label: 'IN PRODUCTION',       icon: Package,       color: 'text-blue-400',   bg: 'bg-blue-400/10' },
  packed:              { label: 'PACKED',              icon: Package,       color: 'text-indigo-400', bg: 'bg-indigo-400/10' },
  dispatched:          { label: 'DISPATCHED',          icon: Truck,         color: 'text-purple-400', bg: 'bg-purple-400/10' },
  delivered:           { label: 'DELIVERED',           icon: Archive,       color: 'text-primary',    bg: 'bg-primary/10' },
  completed:           { label: 'COMPLETED',           icon: CheckCircle2,  color: 'text-primary',    bg: 'bg-primary/10' },
  refunded:            { label: 'REFUNDED',            icon: RotateCcw,     color: 'text-orange-400', bg: 'bg-orange-400/10' },
  partially_refunded:  { label: 'PART. REFUNDED',      icon: RotateCcw,     color: 'text-orange-400', bg: 'bg-orange-400/10' },
};

const ALL_STATUSES = ['pending', 'paid', 'failed', 'in_production', 'packed', 'dispatched', 'delivered', 'completed', 'refunded', 'partially_refunded'];

const TAB_ITEMS = [
  { key: 'notes',     label: 'Notes',     icon: StickyNote },
  { key: 'shipment',  label: 'Shipment',  icon: Truck },
  { key: 'refund',    label: 'Refund',    icon: RotateCcw },
  { key: 'downloads', label: 'Downloads', icon: Download },
  { key: 'audit',     label: 'Audit Log', icon: FileText },
];

export default function AdminOrderCard({ order, userEmail, defaultExpanded = false }) {
  const queryClient = useQueryClient();
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [activeTab, setActiveTab] = useState('notes');

  const status = STATUS_CONFIG[order.status] || STATUS_CONFIG.pending;
  const Icon = status.icon;

  const refreshOrders = () => queryClient.invalidateQueries({ queryKey: ['admin-orders', userEmail] });

  const handleStatusChange = async (e) => {
    const newStatus = e.target.value;
    setSaving(true);
    await base44.functions.invoke('adminUpdateOrder', {
      orderId: order.id,
      patch: { status: newStatus },
      auditAction: 'status_change',
      auditDescription: `Status changed to ${newStatus}`,
      auditOldValue: order.status,
      auditNewValue: newStatus,
    });
    await refreshOrders();
    setSaving(false);
  };

  return (
    <div className="bg-[#0d0d0f] border border-white/5">
      {/* Header */}
      <div className="p-4 space-y-3">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-white font-bold text-sm">{order.product_name}</p>
            <p className="text-white/30 text-xs mt-0.5">
              #{order.id?.slice(-8).toUpperCase()} · {order.created_date ? format(new Date(order.created_date), 'dd MMM yyyy') : '—'}
            </p>
            {order.reconciled && (
              <span className="inline-flex items-center gap-1 text-[9px] font-bold text-yellow-400 bg-yellow-400/10 px-2 py-0.5 mt-1">
                <AlertTriangle className="w-2.5 h-2.5" /> RECONCILED
              </span>
            )}
          </div>
          <span className={`flex items-center gap-1.5 text-[10px] font-bold tracking-wider px-2.5 py-1 shrink-0 ${status.bg} ${status.color}`}>
            <Icon className="w-3 h-3" /> {status.label}
          </span>
        </div>

        <div className="flex items-center justify-between pt-2 border-t border-white/5">
          <span className="text-white/40 text-xs">Total</span>
          <div className="flex items-center gap-3">
            {order.invoice_url && (
              <a
                href={order.invoice_url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1.5 text-primary hover:text-primary/80 transition-colors"
                title="Download invoice"
              >
                <FileText className="w-3.5 h-3.5" />
                <span className="text-[10px] font-bold tracking-wider">INVOICE</span>
              </a>
            )}
            <span className="text-primary font-black text-lg">£{order.amount?.toFixed(2)}</span>
          </div>
        </div>
        {order.refunded_amount > 0 && (
          <div className="flex items-center justify-between">
            <span className="text-orange-400/60 text-xs">Refunded</span>
            <span className="text-orange-400 font-bold text-sm">−£{order.refunded_amount?.toFixed(2)}</span>
          </div>
        )}

        {/* Status changer */}
        <div className="pt-2 border-t border-white/5">
          <div className="flex items-center gap-2">
            <select
              value={order.status}
              onChange={handleStatusChange}
              disabled={saving}
              className="flex-1 bg-[#111115] border border-white/10 text-white text-xs font-bold h-9 px-3 focus:outline-none focus:border-primary/60 transition-colors disabled:opacity-50"
            >
              {ALL_STATUSES.map((s) => (
                <option key={s} value={s} className="bg-[#111115]">
                  {STATUS_CONFIG[s]?.label || s}
                </option>
              ))}
            </select>
            {saving
              ? <Loader2 className="w-4 h-4 animate-spin text-primary shrink-0" />
              : <button onClick={() => setExpanded(o => !o)} className="text-white/30 hover:text-white transition-colors p-1 shrink-0" title="Expand order">
                  {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </button>
            }
          </div>
        </div>
      </div>

      {/* Expanded panel */}
      {expanded && (
        <div className="border-t border-white/5">
          {/* Tabs */}
          <div className="flex overflow-x-auto border-b border-white/5">
            {TAB_ITEMS.map((tab) => {
              const TabIcon = tab.icon;
              return (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`flex items-center gap-1.5 px-4 py-2.5 text-[10px] font-bold tracking-wider whitespace-nowrap transition-colors ${
                    activeTab === tab.key ? 'text-primary border-b-2 border-primary' : 'text-white/30 hover:text-white/60'
                  }`}
                >
                  <TabIcon className="w-3 h-3" /> {tab.label}
                </button>
              );
            })}
          </div>

          <div className="p-4">
            {activeTab === 'notes'     && <OrderInternalNotes orderId={order.id} />}
            {activeTab === 'shipment'  && (
              <div className="space-y-3">
                <RoyalMailDispatchPanel order={order} onSaved={refreshOrders} />
                <OrderShipmentPanel orderId={order.id} onSaved={refreshOrders} />
              </div>
            )}
            {activeTab === 'refund'    && <OrderRefundPanel order={order} onRefunded={refreshOrders} />}
            {activeTab === 'downloads' && <OrderDownloadsPanel orderId={order.id} customerEmail={order.customer_email} />}
            {activeTab === 'audit'     && <OrderAuditLogPanel orderId={order.id} />}
          </div>
        </div>
      )}
    </div>
  );
}